import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CalendarProps {
  selected?: Date;
  onSelect?: (date: Date | undefined) => void;
  disabled?: boolean;
  mode?: 'single' | 'range' | 'multiple';
  className?: string;
}

export const Calendar = ({ selected, onSelect, disabled = false, className = '' }: CalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const onDateClick = (day: Date) => {
    if (disabled) return;
    onSelect?.(day);
  };

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const renderHeader = () => {
    return (
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          aria-label="Previous month"
          onClick={prevMonth}
          className="p-2 rounded-full hover:bg-gray-100 focus:outline-none"
          disabled={disabled}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="text-lg font-semibold">
          {format(currentMonth, 'MMMM yyyy')}
        </div>
        <button
          type="button"
          aria-label="Next month"
          onClick={nextMonth}
          className="p-2 rounded-full hover:bg-gray-100 focus:outline-none"
          disabled={disabled}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    );
  };

  const renderDays = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return (
      <div className="grid grid-cols-7 mb-2">
        {days.map((day) => (
          <div key={day} className="text-center text-sm font-medium text-gray-500">
            {day}
          </div>
        ))}
      </div>
    );
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows: React.ReactNode[] = [];
    let days: React.ReactNode[] = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = new Date(day);
        days.push(
          <div
            key={day.toString()}
            className={`py-2 px-3 text-center cursor-pointer ${
              !isSameMonth(day, monthStart)
                ? 'text-gray-400'
                : selected && isSameDay(day, selected)
                ? 'bg-blue-500 text-white rounded-full'
                : 'hover:bg-gray-100 rounded-full'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => onDateClick(cloneDay)}
          >
            {format(day, 'd')}
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day.toString()} className="grid grid-cols-7">
          {days}
        </div>
      );
      days = [];
    }
    return <div>{rows}</div>;
  };

  return (
    <div className={`p-4 bg-white rounded-lg shadow ${className}`}>
      {renderHeader()}
      {renderDays()}
      {renderCells()}
    </div>
  );
};
